import {Loader, Modal, Select, Text} from '@mantine/core';
import type {ForwardRefExoticComponent, RefAttributes} from 'react';
import type {ReportType} from '../../../../../../core/entities/reportAnalytics/schema';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ModalCancelButton} from '../ModalCancelButton';
import {ModalSubmitButton} from '../ModalSubmitButton';
import {SelectedInfoBox} from './SelectedInfoBox';

/**
 * モーダル設定定数
 */
const MODAL_CONFIG = {
  RADIUS: 16,
  SIZE: 'md' as const,
  TITLE: 'AIでToDoを生成',
} as const;

/**
 * スタイル定数
 */
const STYLES = {
  SELECT_BORDER_RADIUS: '12px',
  SELECT_ICON_WIDTH: 80,
  SELECT_MAX_DROPDOWN_HEIGHT: 300,
} as const;

/**
 * テキスト定数
 */
const TEXT = {
  LOADING_MESSAGE_LINE1: '選考予定に関連するToDoを自動生成しています...',
  LOADING_MESSAGE_LINE2: '結果を表示するにはページに留まってください',
  LOADING_CLOSE_BUTTON: '閉じる',
  SELECT_LABEL: '部門・職種を選択してください',
  SELECT_PLACEHOLDER: '選択してください',
  CANCEL_BUTTON: 'キャンセル',
  SUBMIT_BUTTON: 'ToDoを生成',
  SUBMIT_LOADING_TEXT: '生成中...',
} as const;

/**
 * 部門オプションの型定義
 */
export type DepartmentOption = {
  value: string;
  label: string;
  reportType?: ReportType;
};

type AiTodoGenerateModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: () => void;
  departmentOptions: DepartmentOption[];
  selectedOption: string;
  onSelectChange: (value: string | null) => void;
  isLoading?: boolean;
  itemComponent?: ForwardRefExoticComponent<
    DepartmentOption &
      React.ComponentPropsWithoutRef<'div'> &
      RefAttributes<HTMLDivElement>
  >;
  badgeComponent?: React.ReactNode;
  companyName?: string;
  selectedDepartmentLabel?: string;
  stepName?: string;
};

/**
 * ローディング表示コンポーネント
 */
const LoadingContent: React.FC<{
  onClose: () => void;
  companyName?: string;
  selectedDepartmentLabel?: string;
  stepName?: string;
}> = ({onClose, companyName, selectedDepartmentLabel, stepName}) => (
  <div className="flex flex-col items-center justify-center gap-16">
    <SelectedInfoBox
      companyName={companyName}
      departmentLabel={selectedDepartmentLabel}
      stepName={stepName}
    />
    <Loader size="lg" color="blue" />
    <Text className="text-base text-black-2 text-center">
      {TEXT.LOADING_MESSAGE_LINE1}
      <br />
      {TEXT.LOADING_MESSAGE_LINE2}
    </Text>
    <ModalCancelButton onClick={onClose}>
      {TEXT.LOADING_CLOSE_BUTTON}
    </ModalCancelButton>
  </div>
);

/**
 * フォーム表示コンポーネント
 */
const FormContent: React.FC<{
  departmentOptions: DepartmentOption[];
  selectedOption: string;
  onSelectChange: (value: string | null) => void;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  itemComponent?: ForwardRefExoticComponent<
    DepartmentOption &
      React.ComponentPropsWithoutRef<'div'> &
      RefAttributes<HTMLDivElement>
  >;
  badgeComponent?: React.ReactNode;
  companyName?: string;
  stepName?: string;
}> = ({
  departmentOptions,
  selectedOption,
  onSelectChange,
  onClose,
  onSubmit,
  isLoading,
  itemComponent,
  badgeComponent,
  companyName,
  stepName,
}) => (
  <div className="flex flex-col gap-24">
    <SelectedInfoBox companyName={companyName} stepName={stepName} />
    <div className="flex flex-col gap-8">
      <Text className="text-sm text-black-2">{TEXT.SELECT_LABEL}</Text>
      <Select
        placeholder={TEXT.SELECT_PLACEHOLDER}
        data={departmentOptions}
        value={selectedOption}
        onChange={onSelectChange}
        maxDropdownHeight={STYLES.SELECT_MAX_DROPDOWN_HEIGHT}
        itemComponent={itemComponent}
        icon={badgeComponent}
        iconWidth={STYLES.SELECT_ICON_WIDTH}
        disabled={isLoading}
        withinPortal
        styles={{
          input: {
            borderRadius: STYLES.SELECT_BORDER_RADIUS,
          },
          dropdown: {
            cursor: isLoading ? 'not-allowed' : 'pointer',
          },
        }}
      />
    </div>

    <div className="flex items-center justify-center gap-16">
      <ModalCancelButton onClick={onClose}>
        {TEXT.CANCEL_BUTTON}
      </ModalCancelButton>
      <ModalSubmitButton
        onClick={onSubmit}
        disabled={!selectedOption || isLoading}
        isLoading={isLoading}
        loadingText={TEXT.SUBMIT_LOADING_TEXT}
      >
        {TEXT.SUBMIT_BUTTON}
      </ModalSubmitButton>
    </div>
  </div>
);

/**
 * AI TODO生成モーダル
 * 部門・職種を選択してAI TODOを生成するためのモーダルコンポーネント
 */
export const AiTodoGenerateModal: React.FC<AiTodoGenerateModalProps> = ({
  opened,
  onClose,
  onSubmit,
  departmentOptions,
  selectedOption,
  onSelectChange,
  isLoading = false,
  itemComponent,
  badgeComponent,
  companyName,
  selectedDepartmentLabel,
  stepName,
}) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      radius={MODAL_CONFIG.RADIUS}
      centered
      size={MODAL_CONFIG.SIZE}
      title={MODAL_CONFIG.TITLE}
      zIndex={Z_INDEX.MODAL}
      styles={{
        title: {
          fontWeight: 'bold',
        },
      }}
    >
      {isLoading ? (
        <LoadingContent
          onClose={onClose}
          companyName={companyName}
          selectedDepartmentLabel={selectedDepartmentLabel}
          stepName={stepName}
        />
      ) : (
        <FormContent
          departmentOptions={departmentOptions}
          selectedOption={selectedOption}
          onSelectChange={onSelectChange}
          onClose={onClose}
          onSubmit={onSubmit}
          isLoading={isLoading}
          itemComponent={itemComponent}
          badgeComponent={badgeComponent}
          companyName={companyName}
          stepName={stepName}
        />
      )}
    </Modal>
  );
};
